import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  audioFileIcon,
  designFileIcon,
  excelFileIcon,
  htmlFileIcon,
  imageFileIcon,
  lucidFileIcon,
  pdfFileIcon,
  pptFileIcon,
  videoFileIcon,
  wordFileIcon,
  zipFileIcon,
} from '@g99-product-web-ui/shared-util-lib';

import { FileType } from '../../enums/file-type.enum';
import { FileUtil } from '../../utils/file.util';

@Component({
  selector: 'lib-shared-preview-file',
  templateUrl: './shared-preview-file.component.html',
  styleUrl: './shared-preview-file.component.scss',
  standalone: false,
})
export class SharedPreviewFileComponent implements OnChanges, OnDestroy {
  @Input() public fileUrl?: string;
  @Input() public contentType!: string;
  @Input() public usePreview = false;
  @Input() public file?: File;

  protected defaultFileIcon = lucidFileIcon;
  protected localImagePreview = '';
  protected localVideoPreviewUrl = '';
  protected fileType = FileType;
  protected fileIcons = {
    excel: excelFileIcon,
    csv: excelFileIcon,
    word: wordFileIcon,
    pdf: pdfFileIcon,
    img: imageFileIcon,
    text: wordFileIcon,
    video: videoFileIcon,
    audio: audioFileIcon,
    zip: zipFileIcon,
    code: htmlFileIcon,
    design: designFileIcon,
    ppt: pptFileIcon,
    default: 'assets/unknown-file.svg',
  };

  ngOnChanges(changes: SimpleChanges): void {
    // Generate local preview if a file is provided and it's an image
    if (
      changes['file'] &&
      this.file &&
      FileUtil.isImageFile(this.contentType, this.fileUrl) &&
      this.usePreview
    ) {
      this._generateFilePreview(this.file);
    }
    // Generate local preview if a file is provided and it's a video
    if (
      changes['file'] &&
      this.file &&
      FileUtil.isVideoFile(this.contentType, this.fileUrl) &&
      this.usePreview
    ) {
      this._generateVideoPreview(this.file);
    }
  }

  ngOnDestroy(): void {
    if (this.localVideoPreviewUrl) {
      URL.revokeObjectURL(this.localVideoPreviewUrl);
    }
  }

  protected getOfficeViewerUrl(fileUrl: string) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
  }

  protected getPdfUrl(): string {
    if (!this.fileUrl) {
      return '';
    }

    // Add PDF open parameters to control zoom
    // zoom=150 means 150% zoom
    return `${this.fileUrl}#zoom=300&view=FitH`;

    // Other useful parameters:
    // zoom=150 - Set zoom to 150%
    // view=Fit - Fit entire page in window
    // view=FitH - Fit page width
    // view=FitV - Fit page height
    // page=2 - Open to specific page
  }

  protected getFileType(contentType: string, fileUrl?: string): string {
    if (FileUtil.isImageFile(contentType, fileUrl)) {
      return 'image';
    }
    if (FileUtil.isPdfFile(contentType, fileUrl)) {
      return 'pdf';
    }
    if (FileUtil.isCodeFile(contentType, fileUrl)) {
      return 'code';
    }
    if (FileUtil.isVideoFile(contentType, fileUrl)) {
      return 'video';
    }
    if (FileUtil.isExcelFile(contentType, fileUrl)) {
      return 'excel';
    }
    if (FileUtil.isCsvFile(contentType, fileUrl)) {
      return 'csv';
    }
    if (FileUtil.isTextFile(contentType, fileUrl)) {
      return 'text';
    }
    if (FileUtil.isWordFile(contentType, fileUrl)) {
      return 'word';
    }
    if (FileUtil.isPowerPointFile(contentType, fileUrl)) {
      return 'ppt';
    }
    if (FileUtil.isZipFile(contentType, fileUrl)) {
      return 'zip';
    }
    if (FileUtil.isAudioFile(contentType, fileUrl)) {
      return 'audio';
    }
    if (FileUtil.isDesignFile(contentType, fileUrl)) {
      return 'design';
    }
    return 'default';
  }

  private _generateFilePreview(file: File): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.localImagePreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  private _generateVideoPreview(file: File): void {
    if (this.localVideoPreviewUrl) {
      URL.revokeObjectURL(this.localVideoPreviewUrl);
    }
    this.localVideoPreviewUrl = URL.createObjectURL(file);
  }
}
